import React from "react";
import { motion } from "framer-motion";
import { Users, Building2, CreditCard, Inbox, ShieldAlert, Coffee, UserCheck, Clock,
  TrendingUp, Ban, FileWarning, CircleDollarSign } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
};

const Card = ({ icon: Icon, label, value, accent, sub, testid, onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -2 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
    onClick={onClick} disabled={!onClick}
    data-testid={testid}
    className={`text-left rounded-xl border border-border bg-card p-4 flex flex-col gap-2 ${onClick ? "cursor-pointer hover:border-primary/50" : "cursor-default"}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1a`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
    </div>
    <span className="text-3xl font-bold leading-none" style={{ color: accent }}>{value}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </motion.button>
);

export default function AdminOverview({ data, lang, onGoto }) {
  const L = (it, en) => (lang === "en" ? en : it);
  if (!data) return null;
  const u = data.users || {}, s = data.subscriptions || {}, st = data.structures || {},
    rq = data.requests || {}, dq = data.data_quality || {}, mp = data.meal_plan || {};

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* Utenti */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Utenti", "Users")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={Users} label={L("Utenti totali", "Total users")} value={u.total ?? 0} accent="#2563eb"
            sub={`${u.active ?? 0} ${L("attivi", "active")} · ${u.suspended ?? 0} ${L("sospesi", "suspended")}`}
            testid="ov-users" onClick={() => onGoto("users")} />
          <Card icon={UserCheck} label={L("Owner", "Owners")} value={u.owners ?? 0} accent="#0891b2"
            sub={`${u.revenue_managers ?? 0} revenue mgr · ${u.staff ?? 0} staff`} testid="ov-owners" onClick={() => onGoto("users")} />
          <Card icon={Ban} label={L("Sospesi", "Suspended")} value={u.suspended ?? 0} accent="#d97706" testid="ov-suspended" onClick={() => onGoto("users")} />
          <Card icon={Clock} label={L("In prova", "On trial")} value={u.trial ?? 0} accent="#7c3aed" testid="ov-trial" />
        </div>
      </section>

      {/* Strutture & Abbonamenti */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Strutture & Abbonamenti", "Structures & Subscriptions")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={Building2} label={L("Strutture", "Structures")} value={st.total ?? 0} accent="#16a34a"
            sub={`${st.active ?? 0} ${L("con dati importati", "with imported data")}`} testid="ov-structures" onClick={() => onGoto("properties")} />
          <Card icon={CircleDollarSign} label={L("Abbonamenti attivi", "Active subs")} value={s.active ?? 0} accent="#16a34a"
            sub={`${s.pending_payment ?? 0} ${L("in attesa pagamento", "pending payment")}`} testid="ov-subs-active" onClick={() => onGoto("plans")} />
          <Card icon={TrendingUp} label={L("In scadenza (7gg)", "Expiring (7d)")} value={s.expiring_7d ?? 0} accent="#d97706" testid="ov-subs-expiring" />
          <Card icon={CreditCard} label={L("In prova", "On trial")} value={s.trial ?? 0} accent="#7c3aed" testid="ov-subs-trial" />
        </div>
      </section>

      {/* Da gestire */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Da gestire", "To handle")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={Inbox} label={L("Richieste da approvare", "Pending requests")} value={rq.pending ?? 0}
            accent={rq.pending ? "#ef4444" : "#64748b"} testid="ov-requests" onClick={() => onGoto("requests")} />
          <Card icon={FileWarning} label={L("File da verificare", "Files to verify")} value={dq.files_to_verify ?? 0}
            accent={dq.files_to_verify ? "#d97706" : "#64748b"} testid="ov-files" onClick={() => onGoto("quality")} />
          <Card icon={ShieldAlert} label={L("Alert qualità dati", "Data quality alerts")}
            value={(dq.alerts_red ?? 0) + (dq.alerts_yellow ?? 0)}
            accent={dq.alerts_red ? "#ef4444" : dq.alerts_yellow ? "#d97706" : "#64748b"}
            sub={`${dq.alerts_red ?? 0} 🔴 · ${dq.alerts_yellow ?? 0} 🟡 · ${dq.no_recent_import ?? 0} ${L("senza import", "no import")}`}
            testid="ov-quality" onClick={() => onGoto("quality")} />
          <Card icon={Coffee} label={L("Correzioni tariffe", "Rate corrections")} value={mp.pending_corrections ?? 0}
            accent={mp.pending_corrections ? "#d97706" : "#64748b"} sub={L("in attesa di approvazione", "awaiting approval")}
            testid="ov-mealplan" />
        </div>
      </section>

      {/* Ultimi accessi */}
      <section>
        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{L("Ultimi accessi", "Recent logins")}</h3>
        <div className="rounded-xl border border-border overflow-hidden" data-testid="ov-recent-logins">
          {(data.recent_logins || []).length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">{L("Nessun accesso registrato ancora.", "No logins recorded yet.")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr className="text-left">
                {[L("Utente", "User"), L("Ruolo", "Role"), L("Ultimo accesso", "Last login")].map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.recent_logins.map((r, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`ov-login-${i}`}>
                    <td className="px-4 py-2.5"><span className="font-medium">{r.name || "—"}</span> <span className="text-muted-foreground break-all">· {r.email}</span></td>
                    <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-muted">{r.role}</span></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{fmtDate(r.last_login)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
